/**
 * Дошка воронки (KAN-01, E7b-W16): `/b/<подання>?pipeline=<ключ>&d=<чернетка>` — той самий ключ
 * подання, що й у списку `/l/<подання>`, інший вигляд. Лінивий чанк `platform/board/BoardPage.tsx`
 * зі своїм CSS: у першому екрані лише перемикач «Воронка | Список». Ключ воронки — лише канонічний
 * (`[a-z][a-z0-9_]*`), інше адреса відкидає: дошка тоді відкриває воронку за замовчуванням.
 *
 * `d` — незбережені зміни подання (умова й сортування, W19), той самий кодек, що в списку
 * (view-draft.ts): посиланням дошки з фільтром можна поділитися, а перемикач виглядів несе його далі.
 *
 * `loader` (аудит якості № 1, 24) — за зразком списку (routes/platform.ts): чанк дошки, метадані,
 * конфігурація подання й перші картки видимих стовпців до показу. Роутер тримає попередній екран
 * до готовності дошки, після 300 мс — скелетон дошки, а не порожнеча й три проміжні стани.
 * `preloadBoard` живе в тому самому лінивому чанку, що й екран: окремого чанка `loader` не дає.
 *
 * Залежностей `loader` у пошуку немає (рев'ю QA #885, S6): воронка й чернетка — дані, які дошка,
 * що вже на екрані, вантажить сама (стовпці нової воронки — своїми станами), тож перемикання
 * воронки — той самий збіг маршруту: роутер `loader` на ньому не перезапускає, і `BoardPending` не
 * заміняє дошку з панеллю. Вхід на дошку бере воронку з адреси (`location.search`).
 */
import { createRoute, useParams } from '@tanstack/react-router';
import { Fragment, createElement as h } from 'react';
import { usePlatformMeta } from '../../platform/meta.ts';
import { listSearch } from '../../platform/view-draft.ts';
import { RoutePending } from '../../ui/RoutePending.tsx';
import { SkeletonBoard } from '../../ui/Skeleton.tsx';
import { useRoutePending } from '../route-pending.ts';
import { rootRoute } from './root.tsx';

export interface BoardSearch {
  pipeline?: string;
  d?: string;
}

const PIPELINE_KEY = /^[a-z][a-z0-9_]{0,62}$/;

export function boardSearch(search: Record<string, unknown>): BoardSearch {
  const { d } = listSearch(search);
  return {
    ...(typeof search.pipeline === 'string' && PIPELINE_KEY.test(search.pipeline)
      ? { pipeline: search.pipeline }
      : {}),
    ...(d ? { d } : {}),
  };
}

/**
 * Очікування дошки довше за 300 мс: назва подання на місці, стовпці — смужками (форма дошки, як
 * `BoardSkeleton` екрана). Етапів ще може не бути (чанк і дані — у `loader`), тож стовпців чотири.
 * Фази — маршрутного очікування (`RoutePending`): з 3 с від переходу «Довше, ніж зазвичай…», з
 * 10 с — тайм-аут зі «Спробувати ще раз»; назва подання лишається.
 */
function BoardPending() {
  const { view } = useParams({ from: '/b/$view' });
  const label = usePlatformMeta().data?.views.find((v) => v.key === view)?.label.uk ?? '';
  const pending = useRoutePending();
  return h(
    Fragment,
    null,
    h('div', { className: 'toolbar' }, h('h1', { className: 'toolbar__title' }, label)),
    h(
      'div',
      {
        className: 'content content--canvas board',
        'aria-busy': pending.view === 'skeleton' || undefined,
        'data-testid': 'board-pending',
      },
      h(RoutePending, { state: pending, skeleton: h(SkeletonBoard, { columns: 4 }) }),
    ),
  );
}

const boardChunk = () => import('../../platform/board/BoardPage.tsx');

export const boardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/b/$view',
  validateSearch: boardSearch,
  loader: async ({ context, params, location }) => {
    const board = await boardChunk();
    await board.preloadBoard(
      context.queryClient,
      params.view,
      boardSearch(location.search as Record<string, unknown>),
    );
  },
  pendingComponent: BoardPending,
}).lazy(() => boardChunk().then((m) => m.BoardRoute));
